import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useSWRConfig } from 'swr';
import { fetcher, CACHE_DURATION_MS } from '../utils';
import {
  CHART_OPTIONS,
  CANDLESTICK_SERIES_OPTIONS,
  VOLUME_SERIES_OPTIONS,
  VOLUME_SCALE_OPTIONS,
  convertToChartTime,
  ensureNumber,
  validateAndCleanChartData
} from './chart';
// import { StockService } from '../../services/stockService';
// import { useCustomer } from '../../contexts/CustomerContext';
import '../styles.scss';
import { StockServiceContext } from '../../../context/stock';
import { TickerDataPoint, TickersResponse, TickerInfo } from '../../../providers/stock/types';
import { useRecoilValue } from 'recoil';
import { CustomerAtom } from '../../../atoms/customerAtom';
import { LoginPage } from '../../loginPage';
import { useTickerSearch } from '../../../hooks/useTickerSearch';
import {TickerDataResponse} from '../../../providers/stock/types';

interface ShowCandlestickCharttProps {
  stockData?: TickerDataResponse;
}

export const ShowCandlestickChart: React.FC<ShowCandlestickCharttProps> = ({ stockData }) => {
  const stockDataResponse = stockData || null;

  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const chartRef = React.useRef<IChartApi | null>(null);
  const candlestickSeriesRef = React.useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = React.useRef<ISeriesApi<'Histogram'> | null>(null);

  // Initialize chart on mount
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, CHART_OPTIONS);
    const candlestickSeries = chart.addCandlestickSeries(CANDLESTICK_SERIES_OPTIONS);
    const volumeSeries = chart.addHistogramSeries(VOLUME_SERIES_OPTIONS);

    chart.priceScale('volume').applyOptions(VOLUME_SCALE_OPTIONS);

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;
    volumeSeriesRef.current = volumeSeries;

    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candlestickSeriesRef.current = null;
        volumeSeriesRef.current = null;
      }
    };
  }, []);

  // Update chart when data changes
  useEffect(() => {
    if (!chartRef.current || !candlestickSeriesRef.current || !volumeSeriesRef.current) {
      return;
    }

    if (stockDataResponse?.data) {
      try {
        const validatedData = validateAndCleanChartData(stockDataResponse.data);

        if (validatedData.length === 0) {
          return;
        }

        const candlestickData = validatedData.map((point: TickerDataPoint) => ({
          time: convertToChartTime(point.timestamp as number),
          open: point.open as number,
          high: point.high as number,
          low: point.low as number,
          close: point.close as number,
        }));

        const volumeData = validatedData.map((point: TickerDataPoint) => ({
          time: convertToChartTime(point.timestamp as number),
          value: point.volume as number,
          color: (point.close as number) >= (point.open as number) ? '#26a69a' : '#ef5350',
        }));

        candlestickSeriesRef.current.setData(candlestickData);
        volumeSeriesRef.current.setData(volumeData);
        chartRef.current?.timeScale().fitContent();
      } catch (error) {
        console.error('Error updating chart data:', error);
      }
    }
  }, [stockDataResponse]);

  return (
    <div className="candlestick-chart-wrapper">
      <div
        ref={chartContainerRef}
        className="candlestick-chart"
      />
    </div>
  );
};
